import { Schema } from "effect";
import {
  AccountId,
  ConversationTimelineEventId,
  SupportTicketId,
  TopicId,
  topicIdToSupportTicketId,
} from "../../../../ids/definitions";
import {
  MongoCustomerId,
  MongoRuleId,
  MongoUserId,
} from "../../../../ids/mongo-ids";
import { DateFromDb } from "../../../../schema/helpers";
import {
  CommentActionEventData,
  ConversationInsightsGeneratedEventData,
  ConversationTimelineEvent,
  LegacyTimelineEventData,
  MessageReceivedEventData,
  MessageSentEventData,
  RulesTriggeredEventData,
  SupportTicketAssignedEventData,
  SupportTicketCreatedEventData,
  SupportTicketMergeEventData,
  SupportTicketMigratedFromV1EventData,
  SupportTicketPriorityChangedEventData,
  SupportTicketReadEventData,
  SupportTicketStatusChangedEventData,
  TagAddedEventData,
  TagRemovedEventData,
  ThreadCreatedEventData,
  TimelineActorType,
  TimelineEventMetadata,
  TopicHandoffEventData,
} from "./timeline-event";

const BaseTimelineEventResponseFields = {
  id: ConversationTimelineEventId,
  accountId: AccountId,
  actorType: TimelineActorType,
  actorMongoUserId: Schema.NullOr(MongoUserId),
  actorMongoCustomerId: Schema.NullOr(MongoCustomerId),
  actorMongoRuleId: Schema.NullOr(MongoRuleId),
  parentEventId: Schema.NullOr(ConversationTimelineEventId),
  metadata: Schema.optionalWith(TimelineEventMetadata, { nullable: true }),
  occurredAt: DateFromDb,
};

function makeTimelineEventResponseSchema<
  const IdFields extends Record<string, Schema.Schema<any, any, never>>,
>(idFields: IdFields) {
  const fields = { ...BaseTimelineEventResponseFields, ...idFields };

  return Schema.Union(
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("support_ticket_created"),
      eventData: SupportTicketCreatedEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("support_ticket_status_changed"),
      eventData: SupportTicketStatusChangedEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("support_ticket_priority_changed"),
      eventData: SupportTicketPriorityChangedEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("support_ticket_assigned"),
      eventData: SupportTicketAssignedEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("support_ticket_read"),
      eventData: SupportTicketReadEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("support_ticket_merged"),
      eventData: SupportTicketMergeEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("support_ticket_merge_undone"),
      eventData: SupportTicketMergeEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("topic_handoff"),
      eventData: TopicHandoffEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("message_sent"),
      eventData: MessageSentEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("message_received"),
      eventData: MessageReceivedEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("thread_created"),
      eventData: ThreadCreatedEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("tag_added"),
      eventData: TagAddedEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("tag_removed"),
      eventData: TagRemovedEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("rules_triggered"),
      eventData: RulesTriggeredEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("conversation_insights_generated"),
      eventData: ConversationInsightsGeneratedEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("comment_hidden"),
      eventData: CommentActionEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("comment_unhidden"),
      eventData: CommentActionEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("comment_deleted"),
      eventData: CommentActionEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("comment_liked"),
      eventData: CommentActionEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("comment_un_liked"),
      eventData: CommentActionEventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("support_ticket_migrated_from_v1"),
      eventData: SupportTicketMigratedFromV1EventData,
    }),
    Schema.Struct({
      ...fields,
      eventType: Schema.Literal("legacy_timeline_event"),
      eventData: LegacyTimelineEventData,
    }),
  );
}

export const ConversationTimelineEventResponse =
  makeTimelineEventResponseSchema({ topicId: TopicId });
export type ConversationTimelineEventResponse =
  typeof ConversationTimelineEventResponse.Type;

export const SupportTicketTimelineEventResponse =
  makeTimelineEventResponseSchema({ supportTicketId: SupportTicketId });
export type SupportTicketTimelineEventResponse =
  typeof SupportTicketTimelineEventResponse.Type;

/**
 * Converts a validated ConversationTimelineEvent to the response format.
 */
export function toTimelineEventResponse(
  event: ConversationTimelineEvent,
): ConversationTimelineEventResponse {
  return event;
}

export function toSupportTicketTimelineEventResponse(
  event: ConversationTimelineEvent,
): SupportTicketTimelineEventResponse {
  const { topicId, ...eventWithoutTopicId } = event;

  return {
    ...eventWithoutTopicId,
    supportTicketId: topicIdToSupportTicketId(topicId),
  } as SupportTicketTimelineEventResponse;
}
